import { useEffect, useState } from 'react'
import { Checkbox, Radio } from '@mantine/core'
import dayjs from '@/lib/dayjs'

import { useAxiosApi } from '@/hooks/useAxiosApi'
import { dollars } from '@/components/support/ai/StatTiles'

/**
 * Les modèles proposés pour un agent, avec ce qu'ils ont donné sur cette
 * version et ce qu'ils coûtent. Une seule source pour les deux écrans qui les
 * listent : « Tester » (plusieurs modèles) et « Changer le modèle » (un seul).
 */
export const useEvalModels = (version, agent, reloadKey = null) => {
    const { requestGet } = useAxiosApi()
    const [data, setData] = useState(null)

    // Changer d'agent vide la liste : montrer les modèles de l'autre pendant
    // le chargement afficherait des scores qui ne sont pas les siens.
    useEffect(() => {
        setData(null)
    }, [version, agent])

    // `reloadKey` relit la liste sans la vider — un test qui se termine fait
    // apparaître son score à la place du lien « tester ».
    useEffect(() => {
        let active = true
        requestGet(`/support/ai/prompt-versions/${version}/eval-models`, { agent })
            .then(({ data }) => active && setData(data))
            .catch(() => active && setData(null))
        return () => {
            active = false
        }
    }, [version, agent, reloadKey])

    return data
}

/**
 * Une ligne de modèle : identité, date de sortie, score sur cette version,
 * prix. `control` dit ce qui coche (case ou bouton radio) et `trailing` ce
 * qu'on montre à droite — le seul écart entre les deux écrans.
 */
export const ModelRow = ({ model: m, selected, isConfig, configLabel = 'configuration', onSelect, control = 'checkbox', trailing }) => {
    const Control = control === 'radio' ? Radio : Checkbox

    return (
        <div
            className={`flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 last:border-0 ${
                selected ? 'bg-blue-50/60' : 'hover:bg-gray-50/60'
            }`}
        >
            <button type="button" onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <Control checked={selected} onChange={() => {}} size="xs" tabIndex={-1} aria-hidden />
                <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                        <span className="truncate text-[13px] font-medium text-gray-900">{m.name}</span>
                        {isConfig && (
                            <span className="shrink-0 rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-blue-700">
                                {configLabel}
                            </span>
                        )}
                    </span>
                    <span className="block truncate text-[11px] text-gray-400">
                        {m.released_at ? `sorti en ${dayjs(m.released_at).format('MMM YYYY')}` : 'date de sortie inconnue'}
                        {m.note ? ` · ${m.note}` : ''}
                        {m.unavailable ? ' · absent du catalogue OpenRouter' : ''}
                    </span>
                </span>
            </button>
            {m.on_version && (
                <span
                    className={`shrink-0 text-[11.5px] font-medium tabular-nums ${
                        m.on_version.tasks_passed < m.on_version.tasks_total ? 'text-red-500' : 'text-green-600'
                    }`}
                    title="score obtenu sur cette version"
                >
                    {m.on_version.tasks_passed}/{m.on_version.tasks_total}
                </span>
            )}
            {trailing ?? (
                <span className="w-16 shrink-0 text-right text-[11.5px] text-gray-400" title={m.estimate_basis ?? undefined}>
                    {m.estimate != null ? `≈ ${dollars(m.estimate)}` : '—'}
                </span>
            )}
        </div>
    )
}

/** Le cadre scrollable qui contient les lignes. */
export const ModelList = ({ children }) => (
    <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-gray-200/70">{children}</div>
)
